import * as THREE from 'three';
import store from '../../redux/store';
import { sceneStateHelper } from '../ThreeJsUtils/SceneStateHelper';
import { syncSceneVisibilityFromStore } from '../ThreeJsUtils/assetVisibilityUtils';
import { resetAnimationsToOriginalState } from '../ThreeJsUtils/applyAnimations';
import {
  stopAllSceneAudio,
  playPreviewAudioOnAwake,
  resumeAudioContext,
} from '../audioPlaybackUtils';
import { replayPreviewPhysics } from '../Physics/PhysicsUtils';
import { setHotspotBillboardEnabled } from '../../components/gridpagefunction/HotSpot';
import { resumeSceneParticleAnimations } from '../particles/particleAnimationUtils';
import { clearSceneParticleLifeTimeouts } from '../particles/lifeUtils';
import { syncPreviewAssetEvents } from '../eventAssetUtils';

function replayPreviewVideos(scene) {
  const sceneVideos = store.getState().assets_object?.sceneVideos || [];
  const seenVideos = new Set();

  sceneVideos.forEach((mesh) => {
    if (!mesh) return;

    const video =
      mesh.videoElement ||
      (mesh.material?.map?.image &&
      mesh.material.map instanceof THREE.VideoTexture
        ? mesh.material.map.image
        : null);

    if (!video || typeof video.play !== 'function' || seenVideos.has(video)) {
      return;
    }
    seenVideos.add(video);

    const settings = mesh.userData?.video_settings || {};
    const url =
      mesh.asset_file_path || mesh.userData?.video_settings?.video_clip;

    video.loop = settings.loop ?? false;
    video.muted = settings.mute ?? false;
    video.playbackRate = settings.playback_speed ?? 1;
    video.volume = settings.volume ?? 0.5;
    video.pause();
    video.currentTime = 0;

    const playWhenReady = () => {
      const isAutoPlay =
        settings.play_on_awake === true ||
        String(settings.play_on_awake) === 'true';
      if (isAutoPlay) {
        video.play().catch(() => {});
      }
    };

    if (url && video.src !== url) {
      video.src = url;
      video.load();
      video.addEventListener('canplay', playWhenReady, { once: true });
      video.addEventListener('error', () => {}, { once: true });
    } else {
      playWhenReady();
    }
  });
}

function refreshHotspotBillboards() {
  const hotspotList = store.getState().assets_object?.sceneHotspots || [];
  hotspotList.forEach((hp) => {
    try {
      setHotspotBillboardEnabled(hp, true);
    } catch (_) {}
  });
}

/**
 * Restart the full preview experience (transforms, animations, physics, media).
 * Used by Replay and when entering preview mode.
 */
export function replayPreviewMode({
  scene,
  stopAllAnimations,
  startAllAnimations,
  renderer,
  camera,
} = {}) {
  if (!scene) return;

  if (sceneStateHelper.hasSavedStates()) {
    sceneStateHelper.restoreStates(scene);
  }

  stopAllSceneAudio(scene);
  resumeAudioContext();

  if (typeof stopAllAnimations === 'function') {
    stopAllAnimations();
  }

  const animationObjects =
    store.getState().animation?.animationObjects ||
    store.getState().assets_object?.animationObjects ||
    [];
  if (animationObjects.length) {
    resetAnimationsToOriginalState(scene, animationObjects, {
      restoreTransforms: false,
      restoreMaterials: true,
    });
  }

  replayPreviewPhysics(scene);
  syncPreviewAssetEvents(scene);
  syncSceneVisibilityFromStore(scene);
  replayPreviewVideos(scene);
  playPreviewAudioOnAwake(scene);

  if (typeof startAllAnimations === 'function') {
    startAllAnimations();
  }

  // Full particle lifecycle restart (also used by Replay button).
  clearSceneParticleLifeTimeouts(scene);
  resumeSceneParticleAnimations(scene);

  refreshHotspotBillboards();

  if (renderer && camera) {
    renderer.render(scene, camera);
  }
}
